/**
 * Phone number helpers: per-country live formatting, validation and E.164
 * normalization, backed by libphonenumber-js (the `/min` metadata set keeps the
 * bundle small — enough to validate and format).
 *
 * Pure, UI-agnostic functions — `validatePhone` returns a stable error code and
 * the caller maps it to a localized message (i18n stays in the UI layer). The
 * country is the selected ISO alpha-2 code (e.g. the journey's `phoneCountry`).
 */

import {
  AsYouType,
  isValidPhoneNumber,
  parsePhoneNumber,
  validatePhoneNumberLength,
  type CountryCode,
} from 'libphonenumber-js/min'

/** Validation outcome: a stable error code, or `null` when the number is valid. */
export type PhoneError = 'invalid'

/** Format a number as the user types, following the country's national
 *  convention (e.g. FR "06 12 34 56 78"). Formatting characters in the input are
 *  ignored and re-derived, so feeding back the already-formatted value is safe. */
export function formatPhoneAsYouType(raw: string, country: CountryCode): string {
  return new AsYouType(country).input(raw)
}

/** True when `raw` already holds more digits than the longest valid number for
 *  `country` — lets the input refuse to grow past that length while typing. */
export function isPhoneTooLong(raw: string, country: CountryCode): boolean {
  return validatePhoneNumberLength(raw, country) === 'TOO_LONG'
}

/** Validate a national number for `country`. The empty string is treated as
 *  valid: the phone field is optional and the caller decides if it is required. */
export function validatePhone(raw: string, country: CountryCode): PhoneError | null {
  if (!raw.trim()) return null
  return isValidPhoneNumber(raw, country) ? null : 'invalid'
}

/** Split a stored E.164 number (e.g. "+33612345678") into its ISO country and
 *  national-formatted parts, to pre-fill the phone field (country selector +
 *  national input). Returns `null` when the value isn't a parseable E.164. */
export function fromE164(e164: string): { country: CountryCode; national: string } | null {
  try {
    const parsed = parsePhoneNumber(e164)
    if (!parsed?.country || !parsed.isValid()) return null
    return { country: parsed.country, national: parsed.formatNational() }
  } catch {
    return null
  }
}

/** Canonical E.164 form (e.g. "+33612345678") for persistence, or `null` when
 *  the input can't be parsed into a valid number for the country. */
export function toE164(raw: string, country: CountryCode): string | null {
  try {
    const parsed = parsePhoneNumber(raw, country)
    return parsed?.isValid() ? parsed.number : null
  } catch {
    return null
  }
}
